import { useEffect } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { z } from 'zod'
import { Save } from 'lucide-react'

import type { CreateTransferNoteRequest } from '@/lib/api/product-lot-api'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { PageBackButton } from '@/components/common/PageBackButton'
import { StickyFormFooter } from '@/components/common/StickyFormFooter'
import { TransferNoteItemsField } from '@/components/product-lots/TransferNoteItemsField'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { useCreateProductLotTransferNote } from '@/hooks/use-product-lot-mutation'
import { m } from '@/lib/paraglide/messages'

const itemSchema = z.object({
    source_product_lot_id: z
        .string({ message: m.pdc_err_pick_source() })
        .min(1, m.pdc_err_pick_source()),
    quantity: z
        .number({ message: m.form_err_quantity_required() })
        .positive(m.form_err_quantity_positive()),
    dest_position_id: z
        .string({ message: m.pdc_err_pick_dest() })
        .min(1, m.pdc_err_pick_dest()),
})

const schema = z.object({
    notes: z.string().max(2000, m.form_err_notes_too_long()).optional(),
    items: z.array(itemSchema).min(1, m.form_err_items_min_one()),
})

export type TransferFormData = z.infer<typeof schema>

export const transferFormSchema = schema

type ProductLotTransferFormProps = {
    mode?: 'create' | 'edit'
    attemptKey?: string
    title: string
    submitLabel: string
    isPending: boolean
    defaultValues: TransferFormData
    onSubmit: (
        req: CreateTransferNoteRequest,
        workflow: ReturnType<typeof useWorkflowTracking>,
    ) => Promise<void>
    onCancel: () => void
}

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/sales-warehouse/transfer-notes/new',
)({
    component: NewProductLotTransferNotePage,
})

export function NewProductLotTransferNotePage() {
    const navigate = useNavigate()
    const create = useCreateProductLotTransferNote()

    const handleSubmit = async (
        req: CreateTransferNoteRequest,
        workflow: ReturnType<typeof useWorkflowTracking>,
    ) => {
        const res = await workflow.run(
            () => create.mutateAsync(req),
            (result) => result.data?.id,
        )
        const noteId = (res as { data?: { id?: string } }).data?.id
        if (noteId) {
            void navigate({
                to: '/sales-warehouse/transfer-notes/$id',
                params: { id: noteId },
                replace: true,
            })
        }
    }

    return (
        <ProductLotTransferForm
            title={m.pdc_new_title()}
            submitLabel={m.pdc_action_submit()}
            isPending={create.isPending}
            defaultValues={{
                notes: '',
                items: [
                    {
                        source_product_lot_id: '',
                        quantity: 0,
                        dest_position_id: '',
                    },
                ],
            }}
            onSubmit={handleSubmit}
            onCancel={() =>
                void navigate({
                    to: '/sales-warehouse/transfer-notes',
                    search: { page: 1 },
                })
            }
        />
    )
}

export function ProductLotTransferForm({
    mode = 'create',
    attemptKey,
    title,
    submitLabel,
    isPending,
    defaultValues,
    onSubmit,
    onCancel,
}: ProductLotTransferFormProps) {
    const workflow = useWorkflowTracking('product_transfer', {
        operation: mode === 'edit' ? 'update' : 'create',
        attemptKey,
    })
    const close = () => {
        workflow.finish('closed')
        onCancel()
    }
    const form = useForm<TransferFormData>({
        resolver: zodResolver(schema),
        defaultValues,
    })

    useEffect(() => {
        const subscription = form.watch((_values, { name }) => {
            if (!name) return
            workflow.interact(name.startsWith('items') ? 'items' : 'details')
            if (name.endsWith('_id')) workflow.step('selection_changed')
        })
        return () => subscription.unsubscribe()
    }, [form, workflow])

    const submit = form.handleSubmit(
        async (data) => {
            try {
                await onSubmit(
                    {
                        notes: data.notes || undefined,
                        items: data.items.map((it) => ({
                            source_product_lot_id: it.source_product_lot_id,
                            quantity: Number(it.quantity),
                            dest_position_id: it.dest_position_id,
                        })),
                    },
                    workflow,
                )
            } catch (err) {
                toast.error(
                    err instanceof Error
                        ? err.message
                        : m.sl_new_error_create(),
                )
            }
        },
        () => workflow.step('validation_failed'),
    )

    return (
        <div className="space-y-4 page-pad pb-24 md:pb-6">
            <div className="flex items-center gap-3">
                <PageBackButton onBack={close} />
                <h1 className="text-xl font-semibold">{title}</h1>
            </div>

            <FormProvider {...form}>
                <form
                    onSubmit={submit}
                    className="space-y-3 md:space-y-6 max-w-5xl"
                >
                    <div className="rounded-md border bg-card p-4">
                        <Label>{m.pdc_field_notes()}</Label>
                        <textarea
                            {...form.register('notes')}
                            placeholder={m.sl_new_notes_placeholder()}
                            rows={2}
                            className="mt-1 w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm"
                        />
                    </div>

                    <TransferNoteItemsField />

                    {form.formState.errors.items?.message && (
                        <p className="text-sm text-destructive">
                            {form.formState.errors.items.message}
                        </p>
                    )}

                    <StickyFormFooter>
                        <Button
                            type="button"
                            variant="outline"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            onClick={close}
                        >
                            {m.sl_new_cancel()}
                        </Button>
                        <Button
                            type="submit"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            disabled={isPending}
                        >
                            <Save className="size-4 mr-1" />
                            {isPending ? m.sl_new_submitting() : submitLabel}
                        </Button>
                    </StickyFormFooter>
                </form>
            </FormProvider>
        </div>
    )
}
